import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { FormControl, FormGroup } from '@angular/forms';
import {
  IUserCreate,
  IUserUpdate,
  IUserListResponseApi,
  IUserDetailResponseApi,
  IUserCreateResponseApi,
  IPersonEmployeeListResponseApi,
} from 'src/app/interfaces/users/user.interface';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly http = inject(HttpClient);

  formFilterList = new FormGroup({
    name: new FormControl(''),
    role: new FormControl(''),
    status: new FormControl(''),
    sortBy: new FormControl(''),
    sortOrder: new FormControl(''),
    page: new FormControl(1),
    pageSize: new FormControl(5),
  });

  listAll(payload: {
    page: number;
    pageSize: number;
    name?: string;
    role?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    let params = new HttpParams()
      .set('page', payload.page)
      .set('pageSize', payload.pageSize);

    if (payload.name) params = params.set('name', payload.name);
    if (payload.role) params = params.set('role', payload.role);
    if (payload.status) params = params.set('status', payload.status);
    if (payload.sortBy) params = params.set('sortBy', payload.sortBy);
    if (payload.sortOrder) params = params.set('sortOrder', payload.sortOrder);

    return this.http.get<IUserListResponseApi>(`${environment.api}/user/all`, { params });
  }

  getById(id: string, accessToken?: string) {
    if(accessToken) {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`
      });
      return this.http.get<IUserDetailResponseApi>(`${environment.api}/user/${id}`, { headers });
    } else {
      return this.http.get<IUserDetailResponseApi>(`${environment.api}/user/${id}`);
    }
  }

  create(body: IUserCreate) {
    return this.http.post<IUserCreateResponseApi>(`${environment.api}/user/create`, body);
  }

  update(id: string, body: IUserUpdate) {
    return this.http.put<IUserCreateResponseApi>(`${environment.api}/user/update/${id}`, body);
  }

  export(payload: {
    name?: string;
    role?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
  }) {
    let params = new HttpParams();

    if (payload.name) params = params.set('name', payload.name);
    if (payload.role) params = params.set('role', payload.role);
    if (payload.status) params = params.set('status', payload.status);
    if (payload.sortBy) params = params.set('sortBy', payload.sortBy);
    if (payload.sortOrder) params = params.set('sortOrder', payload.sortOrder);

    return this.http.get(`${environment.api}/user/all/export`, { params, responseType: 'blob' });
  }

  delete(id: string) {
    return this.http.delete(`${environment.api}/user/delete/${id}`).pipe(
      (res) => res,
      (error) => error
    );
  }

  toggle(id: string, status: boolean) {
    return this.http.patch(`${environment.api}/user/${id}/change-status`, { status }).pipe(
      (res) => res,
      (error) => error
    );
  }

  getEmployees() {
    return this.http.get<IPersonEmployeeListResponseApi>(`${environment.api}/person/employees`);
  }

  getEnterprises() {
    return this.http.get<any>(`${environment.api}/user/enterprises/details`, {
      headers: new HttpHeaders().set('x-skip-loading', 'true'),
    }).pipe(
      (res) => res,
      (error) => error
    );
  }
}
